/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { useCallback, useEffect, useState } from 'react';
import { onpremkeyApiUrl, organizationApiUrl } from 'config';
import { OnPremKeyModel, SubscriptionModel } from 'types';
import { get, post, put } from 'utils/http';
import logger from 'utils/logger';

const ORG_API = 'orgs';

export const useOnPremKeySubscription = (
  orgHandler: string
): {
  onPremKeySubscription: SubscriptionModel | null;
  onPremKeySubscriptionError: Error | null;
  isOnPremKeySubscriptionLoading: boolean;
} => {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<boolean>(false);
  const [onPremKeySubscription, setOnPremKeySubscription] =
    useState<SubscriptionModel | null>(null);

  useEffect(() => {
    const getOnPremKeySubscriptionAsyncEffect = async () => {
      setProgress(true);
      try {
        const response = await get(
          `${onpremkeyApiUrl}/${orgHandler}/keys/subscription-info`,
          {},
          true
        );
        if (response?.data) {
          setOnPremKeySubscription(response.data);
        }
      } catch (e) {
        logger.error(e);
        setError(e as Error);
      } finally {
        setProgress(false);
      }
    };
    getOnPremKeySubscriptionAsyncEffect();
  }, [orgHandler]);

  return {
    onPremKeySubscription,
    onPremKeySubscriptionError: error,
    isOnPremKeySubscriptionLoading: progress,
  };
};

export const useGetOnPremKeysList = (
  orgHandler: string
): {
  onPremKeysList: OnPremKeyModel[] | null;
  updateOnPremKeysList: (onPremKey?: OnPremKeyModel) => void;
  deleteOnPremKeyFromList: (onPremKeyHandle: string) => void;
  onPremKeysListError: Error | null;
  isOnPremKeysListLoading: boolean;
} => {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<boolean>(false);
  const [onPremKeysList, setOnPremKeysList] = useState<OnPremKeyModel[] | null>(
    null
  );

  const getOnPremKeysListAsyncEffect = async () => {
    setProgress(true);
    try {
      const response = await get(
        `${onpremkeyApiUrl}/${orgHandler}/keys`,
        {},
        true
      );
      if (response?.data) {
        const keyList = response.data.map(
          (datum: { displayName: any; handle: any; status: any }) => ({
            displayName: datum.displayName,
            handle: datum.handle,
            status: datum.status,
          })
        );
        setOnPremKeysList(keyList);
      }
    } catch (e) {
      logger.error(e);
      setError(e as Error);
    } finally {
      setProgress(false);
    }
  };

  const updateOnPremKeysList = (onPremKey?: OnPremKeyModel) => {
    try {
      if (onPremKey) {
        const existingKey = onPremKeysList?.find(
          (key: OnPremKeyModel) => key.handle === onPremKey.handle
        );
        if (existingKey) {
          const filteredKeys = onPremKeysList?.map((key: OnPremKeyModel) => {
            if (key.handle === onPremKey.handle) {
              return onPremKey;
            }
            return key;
          });
          setOnPremKeysList(filteredKeys || [onPremKey]);
        } else {
          setOnPremKeysList(
            onPremKeysList ? [...onPremKeysList, onPremKey] : [onPremKey]
          );
        }
      } else {
        getOnPremKeysListAsyncEffect();
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const deleteOnPremKeyFromList = (onPremKeyHandle: string) => {
    try {
      if (onPremKeyHandle) {
        const filteredKeys = onPremKeysList?.filter(
          (key: OnPremKeyModel) => key.handle !== onPremKeyHandle
        );
        setOnPremKeysList(filteredKeys || []);
      }
    } catch (e) {
      logger.error(e);
    }
  };

  useEffect(() => {
    getOnPremKeysListAsyncEffect();
  }, [orgHandler]);

  return {
    onPremKeysList,
    updateOnPremKeysList,
    deleteOnPremKeyFromList,
    onPremKeysListError: error,
    isOnPremKeysListLoading: progress,
  };
};

export const useGenerateOnPremKey = (
  orgHandler: string
): {
  generateOnPremKey: (
    configuration: OnPremKeyModel
  ) => Promise<OnPremKeyModel | null>;
  generateOnPremKeyError: Error | null;
  isGenerateOnPremKeyLoading: boolean;
} => {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<boolean>(false);

  const generateOnPremKey = useCallback(
    async (configuration: OnPremKeyModel): Promise<OnPremKeyModel | null> => {
      setProgress(true);
      let onPremKey = null;
      try {
        const response = await post(
          `${onpremkeyApiUrl}/${orgHandler}/keys`,
          null,
          configuration,
          true
        );
        if (response?.data) {
          const { data } = response;
          onPremKey = {
            displayName: data.displayName,
            handle: data.handle,
            status: data.status,
            key: data.key,
          };
        }
      } catch (e) {
        logger.error(e);
        setError(e as Error);
      } finally {
        setProgress(false);
      }
      return onPremKey;
    },
    [orgHandler]
  );

  return {
    generateOnPremKey,
    generateOnPremKeyError: error,
    isGenerateOnPremKeyLoading: progress,
  };
};

export const useRegenerateOnPremKey = (
  orgHandler: string
): {
  regenerateOnPremKey: (keyHandle: string) => Promise<OnPremKeyModel | null>;
  regenerateOnPremKeyError: Error | null;
  isRegenerateOnPremKeyLoading: boolean;
} => {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<boolean>(false);

  const regenerateOnPremKey = useCallback(
    async (keyHandle: string): Promise<OnPremKeyModel | null> => {
      setProgress(true);
      let onPremKey = null;
      try {
        const response = await post(
          `${onpremkeyApiUrl}/${orgHandler}/keys/${keyHandle}/regenerate`,
          null,
          null,
          true
        );
        if (response?.data) {
          const { data } = response;
          onPremKey = {
            displayName: data.displayName,
            handle: data.handle,
            status: data.status,
            key: data.key,
          };
        }
      } catch (e) {
        logger.error(e);
        setError(e as Error);
      } finally {
        setProgress(false);
      }
      return onPremKey;
    },
    [orgHandler]
  );

  return {
    regenerateOnPremKey,
    regenerateOnPremKeyError: error,
    isRegenerateOnPremKeyLoading: progress,
  };
};

export const useEditOnPremKey = (
  orgHandler: string
): {
  editOnPremKey: (
    keyHandle: string,
    configuration: OnPremKeyModel
  ) => Promise<OnPremKeyModel | null>;
  editOnPremKeyError: Error | null;
  isEditOnPremKeyLoading: boolean;
} => {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<boolean>(false);

  const editOnPremKey = useCallback(
    async (
      keyHandle: string,
      configuration: OnPremKeyModel
    ): Promise<OnPremKeyModel | null> => {
      setProgress(true);
      let onPremKey;
      try {
        const response = await put(
          `${onpremkeyApiUrl}/${orgHandler}/keys/${keyHandle}`,
          null,
          configuration,
          true
        );
        if (response) {
          onPremKey = response.data;
        }
      } catch (e) {
        logger.error(e);
        setError(e as Error);
      } finally {
        setProgress(false);
      }
      return onPremKey;
    },
    [orgHandler]
  );

  return {
    editOnPremKey,
    editOnPremKeyError: error,
    isEditOnPremKeyLoading: progress,
  };
};

export const useDeleteOnPremKey = (
  orgHandler: string
): {
  deleteOnPremKey: (keyHandle: string) => Promise<boolean>;
  deleteOnPremKeyError: Error | null;
  isDeleteOnPremKeyLoading: boolean;
} => {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<boolean>(false);

  const deleteOnPremKey = useCallback(
    async (keyHandle: string): Promise<boolean> => {
      setProgress(true);
      let isDeleted = false;
      try {
        const response = await post(
          `${onpremkeyApiUrl}/${orgHandler}/keys/${keyHandle}/revoke`,
          null,
          null,
          true
        );
        if (response) {
          isDeleted = response.data.status === 'REVOKED';
        }
      } catch (e) {
        logger.error(e);
        setError(e as Error);
      } finally {
        setProgress(false);
      }
      return isDeleted;
    },
    [orgHandler]
  );

  return {
    deleteOnPremKey,
    deleteOnPremKeyError: error,
    isDeleteOnPremKeyLoading: progress,
  };
};

export const useGetIsOrgAdmin = (): {
  getIsOrgAdmin: (orgHandler: string) => Promise<boolean>;
  getIsOrgAdminError: Error | null;
  isGetIsOrgAdminLoading: boolean;
} => {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<boolean>(false);

  const getIsOrgAdmin = useCallback(
    async (orgHandler: string): Promise<boolean> => {
      let isAdmin = false;
      setProgress(true);
      try {
        const response = await get(
          `${organizationApiUrl}/${ORG_API}/${orgHandler}`,
          {},
          true
        );
        if (response) {
          isAdmin = response.data.isAdmin || false;
        }
      } catch (e) {
        logger.error(e);
        setError(e as Error);
      } finally {
        setProgress(false);
      }
      return isAdmin;
    },
    []
  );

  return {
    getIsOrgAdmin,
    getIsOrgAdminError: error,
    isGetIsOrgAdminLoading: progress,
  };
};
